/**
 * The whole router. Three pages, one query parameter, no dependency.
 *
 * `?page=threat#dynamics` is the entire vocabulary: the page in the query so it
 * survives a reload and can be linked to, the section in the hash so an (i) can
 * point at the paragraph that expands it rather than at the top of a long page.
 *
 * `LabContext` writes `?branch=` into the same URL with `replaceState`, and
 * rebuilds it from `window.location.href` rather than from its own state, so
 * the two never clobber each other.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type PageId = 'charts' | 'evolution' | 'threat';

export const PAGES: { id: PageId; label: string }[] = [
  { id: 'charts', label: 'Charts' },
  { id: 'evolution', label: 'Understanding evolution' },
  { id: 'threat', label: 'Understanding threat' },
];

const PAGE_PARAM = 'page';
const DEFAULT_PAGE: PageId = 'charts';

function isPageId(value: string | null): value is PageId {
  return PAGES.some((page) => page.id === value);
}

function readLocation(): { page: PageId; section: string } {
  if (typeof window === 'undefined') return { page: DEFAULT_PAGE, section: '' };
  const param = new URLSearchParams(window.location.search).get(PAGE_PARAM);
  return {
    page: isPageId(param) ? param : DEFAULT_PAGE,
    section: window.location.hash.replace(/^#/, ''),
  };
}

interface RouterValue {
  page: PageId;
  /** Hash target, without the `#`. Empty when there is none. */
  section: string;
  navigate: (page: PageId, section?: string) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter must be used inside <RouterProvider>');
  return value;
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(readLocation);

  // Back and forward have to work: a deep page reached from an (i) is exactly
  // the case where the reflex is to hit back rather than to find the nav.
  useEffect(() => {
    const onPopState = () => setLocation(readLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((page: PageId, section = '') => {
    const url = new URL(window.location.href);
    if (page === DEFAULT_PAGE) url.searchParams.delete(PAGE_PARAM);
    else url.searchParams.set(PAGE_PARAM, page);
    url.hash = section ? `#${section}` : '';
    window.history.pushState(null, '', url.toString());
    setLocation({ page, section });
  }, []);

  const value = useMemo<RouterValue>(
    () => ({ page: location.page, section: location.section, navigate }),
    [location, navigate],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

/**
 * The "read more" link an (i) ends with. A button rather than an `<a>`: it is a
 * state change inside one document, and an anchor would offer a right-click
 * "open in new tab" that the query-string router cannot honour from a fresh
 * load of the hash alone.
 */
export function PageLink({
  page,
  section,
  children,
}: {
  page: PageId;
  section?: string;
  children: ReactNode;
}) {
  const { navigate } = useRouter();
  return (
    <button type="button" className="page-link" onClick={() => navigate(page, section)}>
      {children}
      <span aria-hidden="true"> →</span>
    </button>
  );
}
